import React, { memo } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { ChildrenPropType } from '../../../../../../prop-types/children';
import Global from '../../../../../../store/global-selectors';
import { FulfillmentDisplayContext } from '../../../../fulfillment/display-context';
import { useShipmentDisplay } from '../../../../fulfillment/shipment-display/use-shipment-display';

const fulfillmentDetailsSelector = createSelector(
  (state, { fulfillmentId }) => Global.selectEntitiesFulfillmentById(state, { fulfillmentId }) || emptyObject,
  (state, { fulfillmentPlanId }) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) || emptyObject,
  (state) => Global.selectSellerLabelProviders(state) || emptyArray,
  (fulfillment, fulfillmentPlan, sellerLabelProviders) => {
    const packages = (fulfillment.packages?.length ? fulfillment.packages : fulfillmentPlan.packages) || emptyArray;

    const trackingStatus = fulfillment.trackingInformation?.status;
    const shipFromId = fulfillment.shipFromId || fulfillmentPlan.shipFromId;
    const size = fulfillment.dimensions || packages[0]?.dimensions;
    const confirmationEnumId = fulfillment.confirmationEnumId || fulfillmentPlan.options?.confirmationEnumId;
    const insuranceProviderId = fulfillment.insuranceProviderId || fulfillmentPlan.options?.insuranceProviderId;
    const insuranceBookingId = fulfillment.insuranceBookingId;
    const insuranceClaimInitiationUrl = fulfillment.insuranceClaimInitiationUrl;

    const carrierId =
      fulfillment.carrierId ||
      sellerLabelProviders.find(
        (sellerLabelProvider) => sellerLabelProvider.sellerLabelProviderId === fulfillmentPlan.sellerLabelProviderId,
      );
    const shipTo = fulfillmentPlan.shipTo;
    const fulfillmentShipFrom = fulfillment.shipFrom;
    const { availableShippingOptions, options } = fulfillmentPlan;

    return {
      packages,
      shipFromId,
      size,
      carrierId,
      confirmationEnumId,
      confirmationNameFallback: fulfillmentPlan.options?.confirmation,
      insuranceProviderId,
      insuranceProviderFallback: fulfillmentPlan.insuranceProvider,
      trackingStatus,
      shipTo,
      fulfillmentShipFrom,
      availableShippingOptions,
      options,
      insuranceBookingId,
      insuranceClaimInitiationUrl,
    };
  },
);

const FulfillmentDisplayProvider = ({ children, fulfillmentPlanId, fulfillmentId }) => {
  const fulfillmentDetails = useSelector((state) => fulfillmentDetailsSelector(state, { fulfillmentId, fulfillmentPlanId }));

  const value = useShipmentDisplay(fulfillmentDetails);

  return <FulfillmentDisplayContext.Provider value={value}>{children}</FulfillmentDisplayContext.Provider>;
};

export default memo(FulfillmentDisplayProvider);

FulfillmentDisplayProvider.propTypes = {
  children: ChildrenPropType.isRequired,
  fulfillmentId: PropTypes.string.isRequired,
  fulfillmentPlanId: PropTypes.string.isRequired,
};
